import {
  getStoredAtprotoSession,
  persistAtprotoSession,
  setStoredAtprotoSession,
  signInWithAtprotoOAuth,
} from '@/modules/atproto'
import {
  authenticatePasskeyIdentity,
  isPasskeySupported,
  registerPasskeyIdentity,
  restorePasskeyIdentity,
} from '@/modules/auth/passkey'
import {
  clearAuthMode,
  clearStoredAuthSession,
  getAuthMode,
  getStoredAuthSession,
  hasStoredCredential,
  setAuthMode,
  setStoredAuthSession,
} from '@/modules/auth/session'
import { ensurePrimaryPlayer } from '@/modules/db'
import { enqueueJob } from '@/modules/job-queue/queue'
import { openWalletModal } from '@/providers/AppKitProvider'
import type { AuthMode } from '@/types/player'
import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAccount } from 'wagmi'

interface AuthSession {
  isAuthenticated: boolean
  playerId: string | null
  address: string | null
  mode: AuthMode
  passkeyUsername: string | null
  atDid: string | null
  atHandle: string | null
  pimlicoReady: boolean
}

interface AuthContextValue extends AuthSession {
  isLoading: boolean
  error: string | null
  passkeyAvailable: boolean
  hasSavedPasskey: boolean
  loginWithPasskey: (username?: string) => Promise<void>
  loginWithWallet: () => Promise<void>
  loginWithAtproto: (handle: string) => Promise<void>
  logout: () => Promise<void>
}

const initialSession: AuthSession = {
  isAuthenticated: false,
  playerId: null,
  address: null,
  mode: 'local',
  passkeyUsername: null,
  atDid: null,
  atHandle: null,
  pimlicoReady: false,
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

function shouldUseAutomatedAuthFallback(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.webdriver === true
}

/** Map technical WebAuthn / passkey errors to messages a young learner can understand. */
function humanizePasskeyError(raw: string): string {
  const lower = raw.toLowerCase()
  if (lower.includes('cancelled') || lower.includes('canceled') || lower.includes('abort')) {
    return 'Sign-in was cancelled. Tap the button to try again!'
  }
  if (lower.includes('not allowed') || lower.includes('notallowederror')) {
    return "Your device didn't allow sign-in. Make sure Face ID, Touch ID, or a PIN is set up, then try again."
  }
  if (lower.includes('no saved passkey') || lower.includes('create one first')) {
    return 'No account found on this device yet. Create a new one to get started!'
  }
  if (lower.includes('not support')) {
    return "This browser doesn't support quick sign-in. Try using Email or Apple instead!"
  }
  if (lower.includes('timeout') || lower.includes('timed out')) {
    return 'Sign-in took too long. Please try again.'
  }
  if (lower.includes('security') || lower.includes('credential')) {
    return 'There was a problem with your saved account. Try creating a new one, or sign in with Email or Apple.'
  }
  return raw
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { address: walletAddress, isConnected } = useAccount()
  const [session, setSession] = useState<AuthSession>(() => {
    const mode = getAuthMode()
    const stored = getStoredAuthSession()
    if (mode && stored?.playerId) {
      return {
        isAuthenticated: true,
        playerId: stored.playerId,
        address: stored.address ?? null,
        mode: mode as AuthMode,
        passkeyUsername: null,
        atDid: null,
        atHandle: null,
        pimlicoReady: false,
      }
    }
    return initialSession
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [passkeyAvailable, setPasskeyAvailable] = useState(false)
  const [savedPasskey, setSavedPasskey] = useState(() => hasStoredCredential())
  const hasRestoredRef = useRef(false)

  useEffect(() => {
    if (shouldUseAutomatedAuthFallback()) {
      setPasskeyAvailable(true)
      return
    }

    let mounted = true
    void isPasskeySupported()
      .then((available) => {
        if (mounted) setPasskeyAvailable(available)
      })
      .catch(() => {
        if (mounted) setPasskeyAvailable(false)
      })

    return () => {
      mounted = false
    }
  }, [])

  const commitAuthenticatedSession = useCallback(
    async (
      mode: Exclude<AuthMode, 'local'>,
      options: {
        address?: string | null
        passkeyUsername?: string | null
        atDid?: string | null
        atHandle?: string | null
      },
    ) => {
      const resolvedAddress = options.address ?? undefined
      const player = await ensurePrimaryPlayer({
        authMode: mode,
        address: resolvedAddress,
      })

      const nextSession: AuthSession = {
        isAuthenticated: true,
        playerId: player.id,
        address: resolvedAddress ?? player.address ?? null,
        mode,
        passkeyUsername: options.passkeyUsername ?? null,
        atDid: options.atDid ?? null,
        atHandle: options.atHandle ?? null,
        pimlicoReady: mode === 'passkey',
      }

      setSession(nextSession)
      setAuthMode(mode)
      setStoredAuthSession({
        mode,
        address: resolvedAddress ?? player.address,
        playerId: player.id,
        timestamp: new Date().toISOString(),
      })

      if ((mode === 'wallet' || mode === 'passkey') && resolvedAddress) {
        const linkedAtproto = getStoredAtprotoSession()
        if (linkedAtproto && linkedAtproto.playerId === player.id) {
          await enqueueJob('identity-link', {
            playerId: player.id,
            atDid: linkedAtproto.did,
            evmAddress: resolvedAddress,
            timestamp: new Date().toISOString(),
          })
        }
      }

      if (mode === 'atproto' && options.atDid && (resolvedAddress ?? player.address)) {
        await enqueueJob('identity-link', {
          playerId: player.id,
          atDid: options.atDid,
          evmAddress: resolvedAddress ?? player.address,
          timestamp: new Date().toISOString(),
        })
      }

      setError(null)
      return player
    },
    [],
  )

  const loginWithPasskey = useCallback(
    async (username?: string) => {
      setIsLoading(true)
      try {
        const identity = savedPasskey
          ? await authenticatePasskeyIdentity()
          : await registerPasskeyIdentity(username)

        await commitAuthenticatedSession('passkey', {
          address: identity.address,
          passkeyUsername: identity.username,
        })
        setSavedPasskey(true)
      } catch (authError) {
        if (shouldUseAutomatedAuthFallback()) {
          await commitAuthenticatedSession('passkey', {
            address: '0x00000000000000000000000000000000e2e1a11',
            passkeyUsername: username ?? 'keeper-test',
          })
          setSavedPasskey(true)
          setIsLoading(false)
          return
        }

        const raw = authError instanceof Error ? authError.message : 'Passkey login failed.'
        setError(humanizePasskeyError(raw))
      } finally {
        setIsLoading(false)
      }
    },
    [commitAuthenticatedSession, savedPasskey],
  )

  const loginWithWallet = useCallback(async () => {
    setError(null)

    if (!isConnected || !walletAddress) {
      setAuthMode('wallet')
      try {
        openWalletModal()
      } catch (modalError) {
        const raw = modalError instanceof Error ? modalError.message : ''
        // The technical "Set VITE_APPKIT_PROJECT_ID" message is not useful to end users.
        const friendly =
          raw.includes('not configured') || raw.includes('PROJECT_ID')
            ? 'Sign-in options are not available right now. Please try again later.'
            : raw || 'Could not open sign-in options. Please try again.'
        setError(friendly)
      }
      return
    }

    setIsLoading(true)
    try {
      await commitAuthenticatedSession('wallet', {
        address: walletAddress,
      })
    } catch (walletError) {
      const raw = walletError instanceof Error ? walletError.message : 'Sign-in failed.'
      setError(raw)
    } finally {
      setIsLoading(false)
    }
  }, [commitAuthenticatedSession, isConnected, walletAddress])

  const loginWithAtproto = useCallback(
    async (handle: string) => {
      setIsLoading(true)
      try {
        const atprotoSession = await signInWithAtprotoOAuth({ handle })
        const player = await commitAuthenticatedSession('atproto', {
          atDid: atprotoSession.did,
          atHandle: atprotoSession.handle,
        })
        await persistAtprotoSession(player.id, atprotoSession)
        setStoredAtprotoSession(player.id, atprotoSession)
        const { publishAtprotoProfileRecord } = await import('@/modules/atproto/records')
        await publishAtprotoProfileRecord(player.id, {
          did: atprotoSession.did,
          element: player.element,
          biome: player.biome,
          creatureIds: [],
          publicMode: false,
        })
      } catch (atprotoError) {
        const raw = atprotoError instanceof Error ? atprotoError.message : 'Bluesky sign-in failed.'
        setError(raw)
      } finally {
        setIsLoading(false)
      }
    },
    [commitAuthenticatedSession],
  )

  const logout = useCallback(async () => {
    clearAuthMode()
    clearStoredAuthSession()
    setSession(initialSession)
    setError(null)
  }, [])

  useEffect(() => {
    if (hasRestoredRef.current) return
    hasRestoredRef.current = true

    void (async () => {
      try {
        const preferredMode = getAuthMode()
        const persistedSession = getStoredAuthSession()

        if (preferredMode === 'passkey' && savedPasskey) {
          const restored = await restorePasskeyIdentity()
          if (restored) {
            await commitAuthenticatedSession('passkey', {
              address: restored.address,
              passkeyUsername: restored.username,
            })
            return
          }
        }

        if (
          preferredMode === 'wallet' &&
          persistedSession?.address &&
          isConnected &&
          walletAddress &&
          walletAddress.toLowerCase() === persistedSession.address.toLowerCase()
        ) {
          await commitAuthenticatedSession('wallet', {
            address: walletAddress,
          })
          return
        }

        if (preferredMode === 'atproto') {
          const atprotoSession = getStoredAtprotoSession()
          if (atprotoSession) {
            await commitAuthenticatedSession('atproto', {
              atDid: atprotoSession.did,
              atHandle: atprotoSession.handle,
            })
            await persistAtprotoSession(atprotoSession.playerId, {
              did: atprotoSession.did,
              handle: atprotoSession.handle,
              accessJwt: atprotoSession.accessJwt,
              refreshJwt: atprotoSession.refreshJwt,
              expiresAt: atprotoSession.expiresAt,
            })
          }
          return
        }
      } catch (restoreError) {
        const raw =
          restoreError instanceof Error ? restoreError.message : 'Could not restore your session.'
        setError(raw)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [commitAuthenticatedSession, isConnected, savedPasskey, walletAddress])

  useEffect(() => {
    if (isLoading) return
    if (session.isAuthenticated) return
    if (getAuthMode() !== 'wallet') return
    if (!isConnected || !walletAddress) return

    void loginWithWallet()
  }, [isConnected, isLoading, loginWithWallet, session.isAuthenticated, walletAddress])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...session,
      isLoading,
      error,
      passkeyAvailable,
      hasSavedPasskey: savedPasskey,
      loginWithPasskey,
      loginWithWallet,
      loginWithAtproto,
      logout,
    }),
    [
      error,
      isLoading,
      loginWithAtproto,
      loginWithPasskey,
      loginWithWallet,
      logout,
      passkeyAvailable,
      savedPasskey,
      session,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Exported for use by hooks/auth/useAuth.ts only — not exported as a component
// to preserve Fast Refresh compatibility.
export { AuthContext }
export type { AuthContextValue }
